import { Subtitle2, makeStyles, tokens } from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import {
  AreaChart,
  BarChart,
  EmptyState,
  ErrorState,
  KpiTile,
  LoadingState,
  PageHeader,
  SectionCard,
} from '@/design-system';
import { spacing } from '@/theme/tokens';

import { useBusinessSnapshot } from './api';
import { Directory } from './components/Directory';
import { UsersPanel } from './components/UsersPanel';

const useStyles = makeStyles({
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: spacing.m,
    marginBottom: spacing.xxl,
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: spacing.l,
    marginBottom: spacing.xxl,
  },
  asOf: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

const PERCENT = 100;
const SIGNED_BARS_LIMIT = 14;

export function BusinessPage() {
  const classes = useStyles();
  const { data, isLoading, error, refetch } = useBusinessSnapshot();
  const [usersPanelOpen, setUsersPanelOpen] = useState(false);

  const signedDocBars = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.signedDocsByDay.points.slice(-SIGNED_BARS_LIMIT).map((p) => ({
      x: new Date(p.t),
      y: p.v,
    }));
  }, [data]);

  return (
    <div>
      <PageHeader
        title="Business metrics"
        description="Organizations, facilities, users, DAU/MAU, documents, and a directory for live-site lookups."
      />

      {isLoading ? (
        <LoadingState label="Loading business metrics…" />
      ) : error ? (
        <ErrorState
          title="Couldn't load business metrics"
          description={error.message}
          retry={() => {
            void refetch();
          }}
        />
      ) : data?.asOf === 0 ? (
        <EmptyState
          title="No metrics yet"
          description="Business metrics are computed from EHR activity data. The first rollup hasn't run yet — check back in a few minutes."
          action={{ label: 'Refresh', onClick: () => void refetch() }}
        />
      ) : data ? (
        <>
          <span className={classes.asOf}>
            As of {new Date(data.asOf).toLocaleString()}
          </span>

          <div className={classes.kpiGrid} style={{ marginTop: spacing.m }}>
            {data.kpis.map((kpi) => (
              <KpiTile
                key={kpi.key}
                label={kpi.label}
                value={kpi.value}
                previousValue={kpi.previousValue}
                unit={kpi.unit}
                lowerIsBetter={kpi.lowerIsBetter}
                tone="neutral"
                caption={kpi.previousValue === undefined ? undefined : kpi.comparisonLabel}
                onClick={kpi.key === 'activeUsers' ? () => { setUsersPanelOpen(true); } : undefined}
              >
                {kpi.spark ? (
                  <AreaChart
                    series={[kpi.spark]}
                    height={48}
                    hideLegend
                    tone="brand"
                    unit={kpi.unit}
                  />
                ) : null}
              </KpiTile>
            ))}
          </div>

          <Subtitle2 as="h3" style={{ marginBottom: spacing.m }}>
            Activity
          </Subtitle2>
          <div className={classes.twoColumn}>
            <SectionCard title="Documents signed (daily, last 14 days)">
              <BarChart bars={signedDocBars} unit="count" tone="brand" height={220} />
            </SectionCard>
            <SectionCard title="DAU / MAU ratio (30d)">
              <AreaChart
                series={[
                  {
                    name: 'DAU/MAU',
                    points: data.activityRatio.points.map((p) => ({
                      t: p.t,
                      v: p.v * PERCENT,
                    })),
                  },
                ]}
                unit="percent"
                tone="brand"
                height={220}
                hideLegend
              />
            </SectionCard>
          </div>

          <Subtitle2 as="h3" style={{ marginBottom: spacing.m }}>
            Directory
          </Subtitle2>
          <Directory onUsersClick={() => { setUsersPanelOpen(true); }} />
          <UsersPanel open={usersPanelOpen} onClose={() => { setUsersPanelOpen(false); }} />
        </>
      ) : null}
    </div>
  );
}
